/**
 * ctaUtils.js
 *
 * Shared utilities for extracting and resolving CTA data from card content.
 * Used by both NewSingleCard and DetailCard.
 */

/**
 * Extract CTA data from raw HTML for older cards that lack content.extractedData.
 *
 * Extraction priority:
 *  1. data-ad-rendering-role="title"        → ctaHeadline
 *  2. data-ad-rendering-role="description"  → ctaDescription
 *  3. data-ad-rendering-role="cta"          → ctaButtonText
 *  4. data-ad-rendering-role="meta"         → ctaDomain
 *  5. <a data-ad-rendering-role="cta">      → ctaUrl (highest anchor priority)
 *  6. <a> whose text matches button keywords → ctaUrl + ctaButtonText
 *  7. First <a data-ad-rendering-role>      → ctaUrl
 *  8. First <a href> (non-fragment, non-JS) → ctaUrl
 *  9. h1/h2/h3 text                         → ctaHeadline fallback
 * 10. img[alt] text                         → ctaHeadline fallback
 *
 * @param {string} html - raw HTML string (content.html)
 * @returns {{ ctaUrl: string|null, ctaDomain: string|null, ctaHeadline: string|null,
 *             ctaDescription: string|null, ctaButtonText: string|null }}
 */
export function extractCtaFromHtml(html) {
  const result = {
    ctaUrl: null,
    ctaDomain: null,
    ctaHeadline: null,
    ctaDescription: null,
    ctaButtonText: null,
  };

  if (!html) return result;

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");

    // ── 1. data-ad-rendering-role attributes ─────────────────────────────
    const byRole = (role) => doc.querySelector(`[data-ad-rendering-role="${role}"]`);

    const titleEl = byRole("title");
    const descEl  = byRole("description");
    const ctaEl   = byRole("cta");
    const metaEl  = byRole("meta");

    if (titleEl) result.ctaHeadline    = titleEl.textContent?.trim() || null;
    if (descEl)  result.ctaDescription = descEl.textContent?.trim()  || null;
    if (ctaEl)   result.ctaButtonText  = ctaEl.textContent?.trim()   || null;
    if (metaEl)  result.ctaDomain      = metaEl.textContent?.trim()  || null;

    // ── 2. Anchor / CTA link ──────────────────────────────────────────────
    const BUTTON_KEYWORDS =
      /learn more|sign up|shop now|download|get started|buy now|order now|subscribe|try free|start free|book now|contact us|get offer|see offer|apply now/i;

    let ctaAnchor = null;

    // Priority 1: anchor tagged with data-ad-rendering-role="cta"
    if (!ctaAnchor) ctaAnchor = doc.querySelector('a[data-ad-rendering-role="cta"]');

    // Priority 2: any anchor whose visible text matches button keywords
    if (!ctaAnchor) {
      doc.querySelectorAll("a").forEach((a) => {
        if (ctaAnchor) return;
        const text = a.textContent?.trim();
        if (text && BUTTON_KEYWORDS.test(text)) ctaAnchor = a;
      });
    }

    // Priority 3: first anchor with any data-ad-rendering-role
    if (!ctaAnchor) ctaAnchor = doc.querySelector("a[data-ad-rendering-role]");

    // Priority 4: first anchor with a non-fragment, non-javascript href
    if (!ctaAnchor) {
      doc.querySelectorAll("a[href]").forEach((a) => {
        if (ctaAnchor) return;
        const h = a.getAttribute("href") || "";
        if (h && !h.startsWith("#") && !h.startsWith("javascript")) ctaAnchor = a;
      });
    }

    if (ctaAnchor) {
      const href = ctaAnchor.getAttribute("href") || "";
      if (href && !href.startsWith("javascript")) {
        result.ctaUrl = href;
        try {
          result.ctaDomain =
            result.ctaDomain ||
            new URL(href).hostname.replace("www.", "").toUpperCase();
        } catch {
          /* relative URL or malformed — skip */
        }
      }
      // Button text from the anchor itself (only if role didn't already set it)
      if (!result.ctaButtonText) {
        const anchorText = ctaAnchor.textContent?.trim();
        if (anchorText && BUTTON_KEYWORDS.test(anchorText)) {
          result.ctaButtonText = anchorText;
        }
      }
    }

    // ── 3. Heading fallback for headline ──────────────────────────────────
    if (!result.ctaHeadline) {
      const heading = doc.querySelector("h1, h2, h3");
      result.ctaHeadline = heading?.textContent?.trim() || null;
    }

    // ── 4. Image alt text fallback for headline ───────────────────────────
    if (!result.ctaHeadline) {
      const img = doc.querySelector("img[alt]");
      const alt = img?.getAttribute("alt")?.trim();
      if (alt && alt.length > 2) result.ctaHeadline = alt;
    }
  } catch {
    // DOMParser failed — return empty result
  }

  return result;
}

/**
 * Resolve the CTA data for any card (old or new).
 *
 * For **new cards** (content.extractedData exists): reads from aiData / adData.
 * For **old cards** (content.extractedData absent): parses content.html via extractCtaFromHtml.
 *
 * Returns null when there is nothing meaningful to display.
 *
 * @param {{ content: object, aiData: object, adData: object, title: string }} params
 * @returns {{ linkHref: string, domain: string|null, headline: string|null,
 *             description: string|null, buttonText: string|null,
 *             isOldCard: boolean } | null}
 */
export function resolveCtaData({ content, aiData, adData, title }) {
  const isOldCard = !content?.extractedData;

  if (isOldCard) {
    const hasHtmlCta = /data-ad-rendering-role\s*=\s*["']cta["']/i.test(content?.html || "");
    const hasAdDataCta = typeof adData?.["CTA"] === "string"
      ? adData["CTA"].trim() !== ""
      : Boolean(adData?.["CTA"]);

    if (!hasHtmlCta && !hasAdDataCta) return null;

    const extracted = extractCtaFromHtml(content?.html);
    const linkHref = extracted.ctaUrl || content?.sourceUrl || "#";
    const domain =
      extracted.ctaDomain ||
      (() => {
        try {
          return linkHref !== "#"
            ? new URL(linkHref).hostname.replace("www.", "").toUpperCase()
            : null;
        } catch {
          return null;
        }
      })() ||
      null;
    const headline    = extracted.ctaHeadline || adData?.["CTA"] || null;
    const description = extracted.ctaDescription || adData?.["Niche"] || null;
    const buttonText  = extracted.ctaButtonText || null;

    // Nothing useful found — skip CTA section
    if (!domain && !headline && linkHref === "#") return null;

    return { linkHref, domain, headline, description, buttonText, isOldCard: true };
  }

  // ── New card flow ─────────────────────────────────────────────────────
  const ctaDomain      = aiData?.ctaDomain      || null;
  const ctaHeadline    = aiData?.ctaHeadline    || null;
  const ctaDescription = aiData?.ctaDescription || null;
  const ctaUrl         = aiData?.ctaUrl         || content?.sourceUrl || null;

  // If aiData is present but all CTA fields are empty → suppress
  const isAiCtaEmpty =
    aiData &&
    (!aiData.ctaDescription || aiData.ctaDescription === "") &&
    (!aiData.ctaDomain      || aiData.ctaDomain      === "") &&
    (!aiData.ctaHeadline    || aiData.ctaHeadline    === "") &&
    (!aiData.ctaUrl         || aiData.ctaUrl         === "");

  if (isAiCtaEmpty) return null;
  if (!ctaDescription && !ctaDomain && !ctaHeadline && !ctaUrl && !adData?.["CTA"]) return null;

  const linkHref =
    ctaUrl || content?.sourceUrl || "#";
  const domain =
    ctaDomain ||
    (() => {
      try {
        return linkHref !== "#"
          ? new URL(linkHref).hostname.replace("www.", "").toUpperCase()
          : null;
      } catch {
        return null;
      }
    })() ||
    null;
  const headline    = ctaHeadline    || adData?.["CTA"] || title || "Learn More";
  const description = ctaDescription || adData?.["Niche"] || null;
  const buttonText  = null; // new cards always show a fixed "Learn more" button

  return { linkHref, domain, headline, description, buttonText, isOldCard: false };
}
